import React from "react";
import * as Yup from "yup";
import { Formik, FormikProps, FieldProps, Field } from "formik";
import { observable } from "mobx";
import { inject, observer } from "mobx-react";
import { Grid } from "@material-ui/core";
import { Values } from "@src/Tools/Values";
import { IAuthStore } from "@src/Stores/Auth/types";
import { IValuesStore } from "@src/Stores/Values/types";
import { IManagementStore } from "Stores/Management/types";
import { Person, PositionAssignment } from "@src/Interfaces/Entity/Person";
import { Position } from "@src/Interfaces/Entity/Position";
import { Handover, HandoverCandidate } from "@src/Interfaces/Entity/Handover";
import { STFormWrapper } from "@src/Components/Ui/STFormWrapper";
import { STButtonMargin } from "@src/Components/Ui/STButtonMargin";
import FormSimpleSelect from "@src/Components/Common/FormFields/FormSimpleSelect/FormSimpleSelect";
import AppPreloader from "@src/Components/Common/AppPreloader/AppPreloader";
import { SSTFormTitleTypography, ButtonsGrid, STTextField } from "./styles";
import _ from "lodash";
import { Toast } from "@src/Tools/Toast";

const confirmSchema = Yup.object().shape({
  handoverTo: Yup.string().required(() => {
    return Values.getLocalizationValue("fieldRequiredWhenConfirmingHandover");
  })
});

interface IProps {
  values?: IValuesStore;
  management?: IManagementStore;
  auth?: IAuthStore;
  onClose: () => void;
  currentPerson: Person;
  handover: Handover;
  activePositionAssignment: PositionAssignment;
}

interface ISelectItem {
  id: string;
  fullName: string;
}

@inject("values", "management", "auth")
@observer
export default class CreateHandoverForm extends React.Component<IProps> {  
  @observable isLoading: boolean = true;
  handoverCandidates: HandoverCandidate[];
  maySignOut: boolean;
  position: Position;
  toast: Toast = new Toast();

  async componentDidMount() {
    await Promise.all([
      await this.props.management.getHandoverCandidates(),
      await this.props.management.getHandoverMaySignOut(),
    ]).then(x => {
      this.handoverCandidates = x[0];
      this.maySignOut = x[1];
      this.position = _.find(this.props.management.activePositions, x => x.id == this.props.activePositionAssignment.positionId);
      this.isLoading = false;
    });
  }

  async onSubmit(values: Handover) {
    values.position = this.position.id;
    await this.props.management.createHandover(values);
    this.props.onClose();
    this.toast.showSuccess(Values.getLocalizationValue("handoverRequestCreated"));  
  }

  async onSignOutPosition(values: Handover) {
    await this.props.management.doHandoverSignOut(values.creatorNote);
    this.props.onClose();
  }

  render() {
    if (this.isLoading) {
      return <AppPreloader />;
    }

    let initialValues: Handover = new Handover();
    return (
      <Formik
        initialValues={initialValues}
        onSubmit={x => this.onSubmit(x)}
        validationSchema={confirmSchema}
        render={(formikProps: FormikProps<Handover>) => (
          <STFormWrapper onSubmit={formikProps.handleSubmit}>
            <SSTFormTitleTypography>{Values.getLocalizationValue("handover")}</SSTFormTitleTypography>
            <Grid
              container
              spacing={16}
              justify="flex-start"
              alignItems="center">
              <Grid item>
                {Values.getLocalizationValue("position")}: <b>{this.position.name}</b>
              </Grid>
            </Grid>
            <Grid
              container
              spacing={16}
              justify="flex-start"
              alignItems="center"
            >
              <Grid item xs={12}>
                <FormSimpleSelect<Handover, ISelectItem>
                  fieldName="handoverTo"
                  items={this.handoverCandidates}
                  keyAsTitle="fullName"
                  keyAsValue="id"
                  formikProps={formikProps}
                  label={Values.getLocalizationValue("handoverTo")}
                />
              </Grid>
            </Grid>
            <Grid
              container
              spacing={16}
              justify="flex-start"
              alignItems="center"
            >
              <Grid item xs={12}>
                <Field
                  name="creatorNote"
                  render={({ field }: FieldProps) => (
                    <STTextField
                      {...field}
                      fullWidth
                      multiline
                      rows="5"
                      label={Values.getLocalizationValue("addNote")}
                    />
                  )}
                />
              </Grid>
            </Grid>            
            <ButtonsGrid container justify="center">
              {this.maySignOut ?
                <STButtonMargin
                  variant="contained"
                  color="secondary"
                  onClick={() => this.onSignOutPosition(formikProps.values)}>
                  {Values.getLocalizationValue("signOutPosition")}
                </STButtonMargin> : <></>}
              <STButtonMargin
                onClick={formikProps.handleSubmit}
                variant="contained"
                color="primary">
                {Values.getLocalizationValue("confirmHandover")}
              </STButtonMargin>
              <STButtonMargin
                onClick={this.props.onClose}
                variant="contained">
                {Values.getLocalizationValue("cancel")}
              </STButtonMargin>
            </ButtonsGrid>
          </STFormWrapper>
        )}
      />
    );
  }
}
